import axios from 'axios';

// ============================================================
// Axios instance — base URL from .env, Bearer token auto-injected
// ============================================================
const API = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
    timeout: 30000,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

// ── Request interceptor: attach token ────────────────────────
API.interceptors.request.use((config) => {
    const token = localStorage.getItem('ista_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// ── Response interceptor: handle 401 ─────────────────────────
API.interceptors.response.use(
    (res) => res,
    (err) => {
        if (err.response && err.response.status === 401) {
            const url = err.config?.url || '';
            if (!url.includes('/login') && !url.includes('/register')) {
                localStorage.removeItem('ista_token');
                localStorage.removeItem('ista_user');
                window.location.href = '/login';
            }
        }
        return Promise.reject(err);
    }
);

// ============================================================
// AUTH API
// ============================================================
export const authAPI = {
    login:    (data) => API.post('/login', data),
    register: (data) => API.post('/register', data),
    logout:   ()     => API.post('/logout'),
    me:       ()     => API.get('/me'),

    // Profile management
    updateProfile:  (data) => API.put('/profile', data),
    changePassword: (data) => API.put('/profile/password', data),
};

// ============================================================
// PUBLIC API — no auth needed
// ============================================================
export const publicAPI = {
    getFilieres:       (params)      => API.get('/filieres', { params }),
    getFiliereModules: (filiereId)   => API.get(`/filieres/${filiereId}/modules`),
    getModules:        (params)      => API.get('/modules', { params }),
    getJobOffers:      (params)      => API.get('/job-offers', { params }),
    getJobOffer:       (id)          => API.get(`/job-offers/${id}`),
    getIstas:          ()            => API.get('/istas'),
};

// ============================================================
// TRAINER API — prefix: /trainer
// ============================================================
export const trainerAPI = {
    getCandidatures:   ()      => API.get('/trainer/candidatures'),
    getCandidature:    (id)    => API.get(`/trainer/candidatures/${id}`),
    applyCandidature:  (data)  => API.post('/trainer/candidatures', data),
    updateCandidature: (id, d) => API.put(`/trainer/candidatures/${id}`, d),
    deleteCandidature: (id)    => API.delete(`/trainer/candidatures/${id}`),

    uploadDocument: (data) => API.post('/trainer/documents', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    deleteDocument: (id) => API.delete(`/trainer/documents/${id}`),
};

// ============================================================
// DIRECTOR API — prefix: /director
// ============================================================
export const directorAPI = {
    // Offres
    getJobOffers:    ()      => API.get('/director/job-offers'),
    createJobOffer:  (data)  => API.post('/director/job-offers', data),
    updateJobOffer:  (id, d) => API.put(`/director/job-offers/${id}`, d),
    publishJobOffer: (id)    => API.patch(`/director/job-offers/${id}/publish`),
    closeJobOffer:   (id)    => API.patch(`/director/job-offers/${id}/close`),
    deleteJobOffer:  (id)    => API.delete(`/director/job-offers/${id}`),

    // Demandes (candidatures)
    getCandidatures:    ()      => API.get('/director/candidatures'),
    getCandidature:     (id)    => API.get(`/director/candidatures/${id}`),
    reviewCandidature:  (id, d) => API.patch(`/director/candidatures/${id}`, d),
    validateDRH:        (id, d) => API.patch(`/director/candidatures/${id}/validate-drh`, d),
    getCandidatureDocs: (id)    => API.get(`/director/candidatures/${id}/documents`),

    // Filières
    getFilieres:       (params) => API.get('/director/filieres', { params }),
    createFiliere:     (data)  => API.post('/director/filieres', data),
    getFiliere:        (id)    => API.get(`/director/filieres/${id}`),
    updateFiliere:     (id, d) => API.put(`/director/filieres/${id}`, d),
    deleteFiliere:     (id)    => API.delete(`/director/filieres/${id}`),

    // Modules
    getModules:    (params) => API.get('/director/modules', { params }),
    createModule:  (data)  => API.post('/director/modules', data),
    getModule:     (id)    => API.get(`/director/modules/${id}`),
    updateModule:  (id, d) => API.put(`/director/modules/${id}`, d),
    deleteModule:  (id)    => API.delete(`/director/modules/${id}`),
};

// ============================================================
// ADMIN API — prefix: /admin
// ============================================================
export const adminAPI = {
    // Users
    getUsers:    ()       => API.get('/admin/users'),
    createUser:  (data)   => API.post('/admin/users', data),
    toggleUser:  (id)     => API.patch(`/admin/users/${id}/toggle`),
    assignIsta:  (id, d)  => API.patch(`/admin/users/${id}/assign-ista`, d),

    // ISTAs
    getIstas:    ()       => API.get('/admin/istas'),
    createIsta:  (data)   => API.post('/admin/istas', data),
    getIsta:     (id)     => API.get(`/admin/istas/${id}`),
    updateIsta:  (id, d)  => API.put(`/admin/istas/${id}`, d),
    deleteIsta:  (id)     => API.delete(`/admin/istas/${id}`),
};

// ============================================================
// Download helpers
// ============================================================
export const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

export const downloadDocument = (id) =>
    API.get(`/documents/${id}/download`, { responseType: 'blob' });

export default API;
